import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { computeBannerState, deriveUsageState, withTeamSeat } from "../lib/usage";
import { addPendingFreeUsage, usedSecondsWithPending } from "../lib/freeMinutes";
import { selectEmail, selectIsLoggedIn } from "../Redux/authSlice";
import { useGetMyMembershipQuery } from "../Redux/orgApi";
import { isTeamActive } from "../pages/Team/teamStatus";

const DISMISS_PREFIX = "zt_usage_banner_dismissed_";
const REFRESH_DEBOUNCE_MS = 5000;
const MIN_REFRESH_INTERVAL_MS = 30_000;

const UsageContext = createContext(null);

const readDismissed = (severity) => {
  if (!severity) return false;
  try {
    return sessionStorage.getItem(DISMISS_PREFIX + severity) === "true";
  } catch {
    return false;
  }
};

const writeDismissed = (severity) => {
  if (!severity) return;
  try {
    sessionStorage.setItem(DISMISS_PREFIX + severity, "true");
  } catch {
    /* ignore */
  }
};

export const UsageProvider = ({ children }) => {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  // True once the first fetch has settled (success or failure), so pages can
  // tell "no subscription data" apart from "not loaded yet".
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [dismissedVersion, setDismissedVersion] = useState(0);
  const [pendingVersion, setPendingVersion] = useState(0);
  const scope = useSelector(selectEmail);

  const apiBase = useMemo(
    () => (process.env.REACT_APP_BACKEND_URL || "http://localhost:5050").replace(/\/$/, ""),
    []
  );

  const inFlightRef = useRef(null);
  const lastFetchAtRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const fetchUsage = useCallback(async ({ force = false } = {}) => {
    if (inFlightRef.current) return inFlightRef.current;
    if (
      !force &&
      lastFetchAtRef.current > 0 &&
      Date.now() - lastFetchAtRef.current < MIN_REFRESH_INTERVAL_MS
    ) {
      return Promise.resolve();
    }
    const ctrl = new AbortController();
    const promise = (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiBase}/api/subscription/info`, {
          method: "GET",
          credentials: "include",
          signal: ctrl.signal,
        });
        const json = await res.json();
        if (!res.ok || !json?.success) {
          throw new Error(json?.error || "Failed to load usage data.");
        }
        const record = Array.isArray(json?.data) ? json.data[0] : null;
        setRaw(record || null);
        lastFetchAtRef.current = Date.now();
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message || "Failed to load usage data.");
      } finally {
        setLoading(false);
        if (!ctrl.signal.aborted) setLoaded(true);
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = promise;
    promise.cancel = () => ctrl.abort();
    return promise;
  }, [apiBase]);

  const refresh = useCallback(
    (opts = {}) => fetchUsage(opts),
    [fetchUsage]
  );

  const refreshDebounced = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      fetchUsage({ force: true });
    }, REFRESH_DEBOUNCE_MS);
  }, [fetchUsage]);

  // Minutes are also spent in the desktop and mobile apps, so refetch when
  // the user comes back to this tab. The 30 s minimum interval still applies.
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState === "visible") fetchUsage();
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [fetchUsage]);

  useEffect(() => {
    fetchUsage();
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (inFlightRef.current?.cancel) inFlightRef.current.cancel();
    };
  }, [fetchUsage]);

  // Team seat: Pro whatever the personal plan says. Joining or leaving a team
  // (or the team pausing) changes the minutes too, so reload them then.
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { data: teamData } = useGetMyMembershipQuery(undefined, { skip: !isLoggedIn });
  const team = teamData?.membership;
  const teamName = team && isTeamActive(team) ? team.orgName : null;
  const teamKey = team ? `${team.orgId}:${team.orgStatus}` : "none";
  const lastTeamKeyRef = useRef(null);
  useEffect(() => {
    if (!teamData) return;
    if (lastTeamKeyRef.current !== null && lastTeamKeyRef.current !== teamKey) fetchUsage({ force: true });
    lastTeamKeyRef.current = teamKey;
  }, [teamData, teamKey, fetchUsage]);

  // The server's figure, plus any realtime dictation it may not have billed
  // yet (see lib/freeMinutes.js); a team seat then lifts the free limit.
  const usage = useMemo(() => {
    void pendingVersion;
    if (!raw) return null;
    const serverUsed = Math.max(0, Number(raw.UtilizeSecond) || 0);
    const used = usedSecondsWithPending(scope, serverUsed, raw.EndDate);
    return withTeamSeat(deriveUsageState(used === serverUsed ? raw : { ...raw, UtilizeSecond: used }), teamName);
  }, [raw, scope, pendingVersion, teamName]);

  // A realtime dictation just finished: count it now rather than waiting for
  // the relay's billing to reach the server. Free accounts see it in the
  // minutes left, Pro accounts in the minutes used.
  const noteRealtimeDictation = useCallback(
    (seconds) => {
      if (!usage || !(seconds > 0)) return;
      addPendingFreeUsage(scope, seconds, usage.usedSec, raw?.EndDate);
      setPendingVersion((v) => v + 1);
    },
    [usage, scope, raw]
  );
  const bannerState = useMemo(() => computeBannerState(usage), [usage]);

  const bannerDismissed = useMemo(() => {
    // Reading sessionStorage on render; dismissedVersion forces re-eval after dismiss()
    void dismissedVersion;
    return bannerState ? readDismissed(bannerState.severity) : false;
  }, [bannerState, dismissedVersion]);

  const dismissBanner = useCallback(() => {
    if (!bannerState) return;
    writeDismissed(bannerState.severity);
    setDismissedVersion((v) => v + 1);
  }, [bannerState]);

  const value = useMemo(
    () => ({
      raw,
      usage,
      loading,
      loaded,
      error,
      bannerState,
      bannerDismissed,
      refresh,
      refreshDebounced,
      dismissBanner,
      noteRealtimeDictation,
    }),
    [
      noteRealtimeDictation,
      raw,
      usage,
      loading,
      loaded,
      error,
      bannerState,
      bannerDismissed,
      refresh,
      refreshDebounced,
      dismissBanner,
    ]
  );

  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
};

export const useUsage = () => {
  const ctx = useContext(UsageContext);
  if (!ctx) {
    throw new Error("useUsage must be used within a <UsageProvider>");
  }
  return ctx;
};
